import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Live, on-screen speech-to-text from the browser's own recogniser (Chrome,
 * Edge, Safari — not Firefox). A PREVIEW only: the recording made alongside it
 * by useDictation is what Gemini transcribes on submit, and that is the
 * accurate copy. This one exists so the speaker sees their words appear.
 *
 * Chrome ends a "continuous" session on its own after a pause, so while the
 * caller still wants text we restart it and carry the finished text over.
 */

const Recognition = typeof window !== "undefined"
  ? window.SpeechRecognition || window.webkitSpeechRecognition
  : undefined;

export const isLiveTranscriptSupported = () => Boolean(Recognition);

// The recogniser cannot run at all — stop retrying and say so. "network" is
// the browser failing to reach its speech service; restarting would only spin.
const FATAL = new Set(["not-allowed", "service-not-allowed", "audio-capture", "language-not-supported", "network"]);
// Sessions that end this fast, this many times running, are failing silently.
const QUICK_END_MS = 1000;
const MAX_QUICK_ENDS = 3;

// Recognisers pad later results with a leading space; collapse every run to one.
const join = (...parts) => parts.join(" ").replace(/\s+/g, " ").trim();

export default function useLiveTranscript() {
  const [text, setText] = useState("");
  const [unavailable, setUnavailable] = useState(!isLiveTranscriptSupported());
  const recRef = useRef(null);
  const wantedRef = useRef(false);
  // Text from sessions that already ended — Chrome restarts wipe event.results.
  const committedRef = useRef("");
  const quickEndsRef = useRef(0);

  const stop = useCallback(() => {
    wantedRef.current = false;
    const rec = recRef.current;
    recRef.current = null;
    if (!rec) return;
    rec.onresult = null;
    rec.onend = null;
    rec.onerror = null;
    try {
      rec.abort();
    } catch {
      // already stopped
    }
  }, []);

  const start = useCallback(() => {
    stop();
    committedRef.current = "";
    quickEndsRef.current = 0;
    setText("");
    if (!Recognition) {
      setUnavailable(true);
      return;
    }
    setUnavailable(false);
    wantedRef.current = true;

    const open = () => {
      const rec = new Recognition();
      rec.continuous = true;
      rec.interimResults = true;
      rec.lang = navigator.language || "en-GB";
      let sessionFinal = "";
      const openedAt = Date.now();

      rec.onresult = (event) => {
        let interim = "";
        sessionFinal = "";
        for (let i = 0; i < event.results.length; i += 1) {
          const piece = event.results[i][0]?.transcript ?? "";
          if (event.results[i].isFinal) sessionFinal += ` ${piece}`;
          else interim += ` ${piece}`;
        }
        setText(join(committedRef.current, sessionFinal, interim));
      };
      rec.onerror = (event) => {
        if (FATAL.has(event.error)) {
          wantedRef.current = false;
          setUnavailable(true);
        }
        // "no-speech" / "aborted": onend follows and restarts.
      };
      rec.onend = () => {
        committedRef.current = join(committedRef.current, sessionFinal);
        setText(committedRef.current);
        quickEndsRef.current = Date.now() - openedAt < QUICK_END_MS ? quickEndsRef.current + 1 : 0;
        if (quickEndsRef.current >= MAX_QUICK_ENDS) {
          wantedRef.current = false;
          setUnavailable(true);
        }
        if (wantedRef.current) open();
      };

      recRef.current = rec;
      try {
        rec.start();
      } catch {
        wantedRef.current = false;
        setUnavailable(true);
      }
    };

    open();
  }, [stop]);

  useEffect(() => stop, [stop]);

  return { text, unavailable, start, stop };
}
